<template>
  <div class="facet-container user-select-none p-0">
    <e-c-facet-header v-if="header" :title="header" :tooltip="toolTipTitle" />
    <treeselect
      v-model="selectedModel"
      :multiple="true"
      :options="filteredTreeOptions"
      :searchable="false"
      :clearable="false"
      :show-count="false"
      :value-consists-of="'ALL'"
      :default-expand-level="0"
      :max-height="400"
      :append-to-body="false"
      placeholder=" - Any - "
      class="ec-tree-select"
      @update:modelValue="onSelectedIdsChange"
      @close="onDropdownClose"
    >
      <template #value-label="{ node }">
        <span class="tree-value-label">
          <span
            v-if="showSelectionBadgeForNode(node)"
            class="tree-select-badge"
          >
            {{ activeSelectionCount }}
          </span>
          <span class="tree-value-text">{{ node.label }}</span>
        </span>
      </template>
      <template #option-label="{ node }">
        <span class="tree-option-row">
          <span class="tree-option-text">{{ node.label }}</span>
          <span
            v-if="node.countLabel || (node.raw && node.raw.countLabel)"
            class="tree-option-count"
            :data-tooltip="isHvdCategoryField ? hvdSelectionCountTooltip : null"
            @mouseenter="showFloatingCountTooltip($event, isHvdCategoryField ? hvdSelectionCountTooltip : null)"
            @mouseleave="hideFloatingCountTooltip"
            @focus="showFloatingCountTooltip($event, isHvdCategoryField ? hvdSelectionCountTooltip : null)"
            @blur="hideFloatingCountTooltip"
          >
            {{ node.countLabel || (node.raw && node.raw.countLabel) }}
          </span>
        </span>
      </template>
      <template #before-list>
        <div
          class="tree-filter-wrapper"
          @mousedown.stop
          @click.stop
          @keydown.stop
          @keyup.stop
        >
          <input
            type="text"
            class="ecl-text-input col"
            :placeholder="filterPlaceholder"
            v-model="filter"
            @mousedown.stop
            @click.stop
            @keydown.stop
            @keyup.stop
          />
        </div>
      </template>
    </treeselect>

    <!-- <details class="tree-debug">
      <summary>HVD Tree Debug (raw facet items)</summary>
      <pre>{{ debugItems }}</pre>
    </details> -->
  </div>
</template>

<script>
import Treeselect from "vue3-treeselect";
import "vue3-treeselect/dist/vue3-treeselect.css";
import { helpers } from "@piveau/piveau-hub-ui-modules";
import ECFacetHeader from "@/components/ECFacetHeader";

const { getFacetTranslation } = helpers;

// Workaround for vue3-treeselect race condition where handleMouseDown can run
// while refs are temporarily unavailable during route-driven rerenders.
if (!Treeselect.__deuNullRefsGuardPatched && Treeselect.methods) {
  const originalHandleMouseDown = Treeselect.methods.handleMouseDown;
  if (typeof originalHandleMouseDown === "function") {
    Treeselect.methods.handleMouseDown = function patchedHandleMouseDown(evt) {
      const valueContainer = this?.$refs?.control?.$refs?.["value-container"];
      if (!valueContainer) return;
      return originalHandleMouseDown.call(this, evt);
    };
  }
  Treeselect.__deuNullRefsGuardPatched = true;
}

export default {
  name: "ECTreeSelect",
  components: {
    ECFacetHeader,
    Treeselect,
  },
  props: {
    header: {
      type: String,
      default: "",
    },
    fieldId: {
      type: String,
      default: "",
    },
    items: {
      type: Array,
      required: true,
    },
    toolTipTitle: {
      type: String,
      default: "",
    },
    facetIsSelected: Function,
    facetClicked: Function,
  },
  data() {
    return {
      filter: "",
      filterPlaceholder: "Filter",
      routeSyncTimer: null,
      selectedModel: [],
      cachedItemsById: {},
      isSyncingFromRoute: false,
      floatingCountTooltipEl: null,
      floatingCountTooltipAnchorEl: null,
    };
  },
  computed: {
    activeSelectionCount() {
      return this.selectedModel.length;
    },
    isHvdCategoryField() {
      return this.fieldId === "hvdCategory";
    },
    hvdSelectionCountTooltip() {
      if (!this.isHvdCategoryField) return null;
      return 'This count shows the number of datasets explicitly assigned to this High-Value Dataset category. The total "Datasets found" may be different because datasets can belong to multiple selected sub-categories, and the search results count each dataset only once.';
    },
    placeholder() {
      return this.i18n.global.t("message.datasetFacets.facets.select") || "- Any -";
    },
    treeOptions() {
      const normalizeId = (value) => String(value);
      const nodesById = {};
      const sourceItems = this.getMergedItems();

      sourceItems.forEach((item) => {
        const nodeId = normalizeId(item.id);
        const label = this.getTitle(item);
        const count = Number.isFinite(item.count) ? item.count.toLocaleString("fi") : "";
        nodesById[nodeId] = {
          id: nodeId,
          label,
          countLabel: count,
        };
      });

      const roots = [];

      sourceItems.forEach((item) => {
        const nodeId = normalizeId(item.id);
        const node = nodesById[nodeId];
        const rawParentId = item.parent_id || item.parent_Id || item.parentId;
        const parentId = rawParentId == null ? null : normalizeId(rawParentId);

        if (parentId && nodesById[parentId]) {
          if (!nodesById[parentId].children) {
            nodesById[parentId].children = [];
          }
          nodesById[parentId].children.push(node);
        } else {
          roots.push(node);
        }
      });

      return roots;
    },
    normalizedFilter() {
      return this.filter.toLowerCase();
    },
    filteredTreeOptions() {
      if (!this.normalizedFilter) return this.treeOptions;
      return this.filterTreeNodes(this.treeOptions, this.normalizedFilter);
    },
    debugItems() {
      return JSON.stringify(this.items, null, 2);
    },
  },
  watch: {
    items: {
      handler(items) {
        this.cacheItems(items);
      },
      deep: true,
      immediate: true,
    },
    "$route.query": {
      handler() {
        this.syncSelectedModelFromRoute();
      },
      deep: true,
    },
  },
  methods: {
    getFacetTranslation,
    cacheItems(items) {
      if (!Array.isArray(items)) return;
      const nextCache = { ...this.cachedItemsById };
      items.forEach((item) => {
        if (!item || item.id == null) return;
        nextCache[this.normalizeId(item.id)] = item;
      });
      this.cachedItemsById = nextCache;
    },
    getMergedItems() {
      const merged = {};
      const currentItems = Array.isArray(this.items) ? this.items : [];

      // Prefer current API facet items so the tree reflects latest backend state.
      currentItems.forEach((item) => {
        if (item && item.id != null) merged[this.normalizeId(item.id)] = item;
      });

      // Keep selected ids visible even if the backend temporarily omits them.
      const selectedIds = this.normalizeRouteSelection();
      selectedIds.forEach((selectedId) => {
        const cachedItem = this.cachedItemsById[selectedId];
        if (cachedItem && !merged[selectedId]) {
          merged[selectedId] = cachedItem;
        }

        // Add cached ancestors so selected nodes can stay in a consistent path.
        let current = cachedItem;
        while (current) {
          const rawParentId = current.parent_id || current.parent_Id || current.parentId;
          if (rawParentId == null) break;
          const parentId = this.normalizeId(rawParentId);
          if (merged[parentId]) break;
          const parent = this.cachedItemsById[parentId];
          if (!parent) break;
          merged[parentId] = parent;
          current = parent;
        }
      });

      return Object.values(merged);
    },
    normalizeId(value) {
      return String(value);
    },
    normalizeRouteSelection() {
      let selected = this.$route.query?.[this.fieldId] || [];
      if (!Array.isArray(selected)) selected = [selected];
      return selected.map(this.normalizeId);
    },
    isOptionsLoadedForSanitizing() {
      return Array.isArray(this.items) && this.items.length > 0;
    },
    sanitizeSelectionAgainstOptions(selection) {
      if (!this.isOptionsLoadedForSanitizing()) return selection;
      const allowedIds = new Set(
        this.getMergedItems().map((item) => this.normalizeId(item.id))
      );
      return selection.filter((id) => allowedIds.has(id));
    },
    replaceRouteSelection(selection) {
      const query = { ...this.$route.query };
      if (selection.length > 0) query[this.fieldId] = selection;
      else delete query[this.fieldId];

      this.$router.replace({ query }).catch((error) => {
        console.error(error);
      });
    },
    arraysEqual(a, b) {
      if (a.length !== b.length) return false;
      return a.every((value, index) => value === b[index]);
    },
    syncSelectedModelFromRoute() {
      const fromRoute = this.normalizeRouteSelection();
      const sanitized = this.sanitizeSelectionAgainstOptions(fromRoute);

      if (!this.arraysEqual(fromRoute, sanitized)) {
        this.replaceRouteSelection(sanitized);
      }

      if (!this.arraysEqual(this.selectedModel, sanitized)) {
        this.isSyncingFromRoute = true;
        this.selectedModel = sanitized;
        this.$nextTick(() => {
          this.isSyncingFromRoute = false;
        });
      }
    },
    onSelectedIdsChange(nextIds) {
      // Ignore model updates triggered by route synchronization to avoid
      // re-adding removed children from selected-facets interactions.
      if (this.isSyncingFromRoute) return;

      const nextArray = Array.isArray(nextIds)
        ? nextIds
        : nextIds == null
          ? []
          : [nextIds];
      const next = new Set(nextArray.map(this.normalizeId));

      const orderedNext = this.items
        .map((item) => this.normalizeId(item.id))
        .filter((id) => next.has(id));

      const currentNormalized = this.normalizeRouteSelection();
      if (this.arraysEqual(currentNormalized, orderedNext)) return;

      if (this.routeSyncTimer) clearTimeout(this.routeSyncTimer);
      this.routeSyncTimer = setTimeout(() => {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              [this.fieldId]: orderedNext,
              page: 1,
            },
          })
          .catch((error) => {
            console.error(error);
          });
        this.routeSyncTimer = null;
      }, 0);
    },
    filterTreeNodes(nodes, searchTerm) {
      return nodes.reduce((acc, node) => {
        const filteredChildren = node.children
          ? this.filterTreeNodes(node.children, searchTerm)
          : [];
        const isMatch = this.optionMatchesFilter(node, searchTerm);

        if (isMatch || filteredChildren.length) {
          acc.push({
            ...node,
            children: filteredChildren.length ? filteredChildren : node.children,
          });
        }

        return acc;
      }, []);
    },
    optionMatchesFilter(node, searchTerm) {
      return String(node.label || "").toLowerCase().includes(searchTerm);
    },
    showSelectionBadgeForNode(node) {
      if (!node || this.activeSelectionCount < 1) return false;
      return this.normalizeId(node.id) === this.selectedModel[0];
    },
    onDropdownClose() {
      this.filter = "";
    },
    getTitle(item) {
      return this.getFacetTranslationWrapper(
        this.fieldId,
        item.id,
        this.$route.query.locale,
        item.title
      );
    },
    getFacetTranslationWrapper(fieldId, facetId, userLocale, fallback) {
      return fieldId === "scoring"
        ? this.i18n.global.t(`message.datasetFacets.facets.scoring.${facetId}`)
        : this.getFacetTranslation(fieldId, facetId, userLocale, fallback);
    },
    updateFloatingCountTooltipPosition() {
      if (!this.floatingCountTooltipEl || !this.floatingCountTooltipAnchorEl) return;

      const anchorRect = this.floatingCountTooltipAnchorEl.getBoundingClientRect();
      const tooltipRect = this.floatingCountTooltipEl.getBoundingClientRect();
      const viewportPadding = 8;

      let left = anchorRect.right - tooltipRect.width;
      left = Math.max(
        viewportPadding,
        Math.min(left, window.innerWidth - tooltipRect.width - viewportPadding)
      );

      let top = anchorRect.top - tooltipRect.height - 8;
      if (top < viewportPadding) {
        top = anchorRect.bottom + 8;
      }

      this.floatingCountTooltipEl.style.left = `${left}px`;
      this.floatingCountTooltipEl.style.top = `${top}px`;
    },
    showFloatingCountTooltip(event, text) {
      if (!text) return;

      this.hideFloatingCountTooltip();

      const tooltipEl = document.createElement("div");
      tooltipEl.className = "tree-select-count-tooltip-floating";
      tooltipEl.textContent = text;
      document.body.appendChild(tooltipEl);

      this.floatingCountTooltipEl = tooltipEl;
      this.floatingCountTooltipAnchorEl = event.currentTarget;

      this.updateFloatingCountTooltipPosition();

      window.addEventListener("scroll", this.updateFloatingCountTooltipPosition, true);
      window.addEventListener("resize", this.updateFloatingCountTooltipPosition);
    },
    hideFloatingCountTooltip() {
      window.removeEventListener("scroll", this.updateFloatingCountTooltipPosition, true);
      window.removeEventListener("resize", this.updateFloatingCountTooltipPosition);

      if (this.floatingCountTooltipEl && this.floatingCountTooltipEl.parentNode) {
        this.floatingCountTooltipEl.parentNode.removeChild(this.floatingCountTooltipEl);
      }

      this.floatingCountTooltipEl = null;
      this.floatingCountTooltipAnchorEl = null;
    },
  },
  beforeUnmount() {
    this.hideFloatingCountTooltip();
    if (this.routeSyncTimer) clearTimeout(this.routeSyncTimer);
  },
  created() {
    this.syncSelectedModelFromRoute();
  },
};
</script>

<style>

.ec-tree-select .tree-option-count[data-tooltip] {
  cursor: pointer;
}

.tree-select-count-tooltip-floating {
  position: fixed;
  width: 360px;
  max-width: min(360px, calc(100vw - 16px));
  background: #000;
  color: #fff;
  font-size: 14px;
  line-height: 1.4;
  padding: 4px 8px;
  border-radius: 4px;
  text-align: left;
  pointer-events: none;
  z-index: 20000;
}
.ec-tree-select .tree-option-count[data-tooltip]:focus-visible::after,
.ec-tree-select .tree-option-count[data-tooltip]:hover::before,
.ec-tree-select .tree-option-count[data-tooltip]:focus-visible::before {
  opacity: 1;
}

.ec-tree-select .ecl-checkbox__label {
  display: inline-flex;
  align-items: flex-start !important;
  width: 100%;
  white-space: normal;
  line-height: 1.4;
}

.ec-tree-select .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__option-arrow-container,
.ec-tree-select .vue-treeselect__option-arrow-placeholder,
.ec-tree-select .vue-treeselect__label {
  vertical-align: top;
}

.vue-treeselect__multi-value-item-container {
    vertical-align: middle;
}


.ec-tree-select .vue-treeselect__label {
  white-space: normal;
  overflow: visible;
  text-overflow: initial;
  line-height: 1.4;
}

.ec-tree-select .vue-treeselect__value-remove {
  color: #5f6b7a;
  cursor: pointer;
  opacity: 1;
  transition: color 0.15s ease;
}

.ec-tree-select .vue-treeselect__value-remove svg,
.ec-tree-select .vue-treeselect__value-remove svg path {
  fill: currentColor;
}

/* Prevent library rule .vue-treeselect__multi-value-item:hover .vue-treeselect__value-remove */
.ec-tree-select .vue-treeselect__multi-value-item:hover .vue-treeselect__value-remove {
  color: #5f6b7a !important;
}

.ec-tree-select .vue-treeselect__value-remove:hover,
.ec-tree-select .vue-treeselect__multi-value-item:hover .vue-treeselect__value-remove:hover,
.ec-tree-select .vue-treeselect__value-remove:focus {
  color: #0e47cb !important;
}

.vue-treeselect__multi-value-item {
  background: none;
  font-size: 16px;
  color: #000;
}

.ec-tree-select .vue-treeselect__multi-value {
  max-height: 41px;
  overflow: hidden;
}

.vue-treeselect__placeholder {
    color: #393939;
    margin-top: 8px;
}

.vue-treeselect__option {
  padding: 10px !important;
  padding-bottom: 20px !important;
}

.ec-tree-select {
  width: 100%;
}

.tree-filter-wrapper {
  position: sticky;
  top: 0;
  z-index: 1;
  pointer-events: none;
  background: #f5f5f5;
  padding-top: 5px;
  padding-bottom: 5px;
  margin-bottom: 10px;
}

.tree-filter-wrapper * {
  pointer-events: auto;
}

.ec-tree-select .tree-filter-wrapper .ecl-text-input {
  width: -webkit-fill-available;
  margin: 10px 10px 10px 10px;
  border-radius: 5px;
  border: 1px solid #707070;
  box-shadow: 0 2px 4px rgba(9, 49, 142, 0.08) inset,
    0 0 10px rgba(9, 49, 142, 0.04) inset,
    0 4px 5px rgba(9, 49, 142, 0.04) inset,
    0 -4px 4px rgba(9, 49, 142, 0.04) inset;
}

.ec-tree-select{
  border: 1px solid #707070;
  border-radius: 4px;
}

.ec-tree-select:hover,
.ec-tree-select.vue-treeselect--focused,
.ec-tree-select.vue-treeselect--open {
  border-color: var(--primary);
}

.vue-treeselect__control {
  border: none;
  min-height: 48px;
}

.ec-tree-select:deep(.vue-treeselect__control:hover) {
  border-color: #707070;
}

.ec-tree-select .vue-treeselect__control {
  padding-right: 0;
}

.ec-tree-select .vue-treeselect__control-arrow-container {
  width: 2.75rem;
  min-width: 2.75rem;
  background: var(--primary);
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  position: relative;
}

.vue-treeselect--open-below .vue-treeselect__menu {
    margin-top: 1px;
    padding-top: 0px;
}

.ec-tree-select .vue-treeselect__control-arrow,
.ec-tree-select .vue-treeselect__control-arrow-container:hover .vue-treeselect__control-arrow {
  width: 0;
  height: 0;
  color: #ffffff;
  opacity: 0;
}

.ec-tree-select .vue-treeselect__control-arrow-container::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 18px;
  height: 18px;
  margin-top: -9px;
  margin-left: -9px;
  transform: rotate(180deg);
  background-repeat: no-repeat;
  background-size: 18px 18px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 48 48'%3E%3Cpath fill='%23ffffff' d='M40 38 24 20 8 38l-6-6L24 8l22 24z'/%3E%3C/svg%3E");
}

.ec-tree-select .vue-treeselect__control-arrow-container:hover,
.ec-tree-select .vue-treeselect__control:hover .vue-treeselect__control-arrow-container {
  background: #3e6cd5;
}

.ec-tree-select:deep(.vue-treeselect__menu) {
  background: #f5f5f5;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
  max-height: 500px;
  overflow-y: auto;
  overflow-x: visible;
  position: relative;
  isolation: isolate;
}

.ec-tree-select:deep(.vue-treeselect__menu-container) {
  z-index: 999;
}

.ec-tree-select:deep(.vue-treeselect__option) {
  background: #ffffff;
  position: relative;
  overflow: visible;
  z-index: 1;
}

.ec-tree-select:deep(.vue-treeselect__option:hover),
.ec-tree-select:deep(.vue-treeselect__option--highlight) {
  z-index: 3000;
}

.ec-tree-select:deep(.vue-treeselect__option--highlight) {
  background: #ffffff;
}

.ec-tree-select:deep(.vue-treeselect__option:hover),
.ec-tree-select:deep(.vue-treeselect__option--highlight:hover),
.ec-tree-select:deep(.vue-treeselect__option--highlight) {
  background: #ffffff !important;
}

.ec-tree-select .vue-treeselect__option,
.ec-tree-select .vue-treeselect__option:hover,
.ec-tree-select .vue-treeselect__option--highlight,
.ec-tree-select .vue-treeselect__option--highlight:hover {
  background: #ffffff !important;
}

.ec-tree-select .vue-treeselect__option:hover > .vue-treeselect__label-container,
.ec-tree-select .vue-treeselect__option--highlight > .vue-treeselect__label-container,
.ec-tree-select .vue-treeselect__option:hover > .vue-treeselect__option-arrow-container,
.ec-tree-select .vue-treeselect__option--highlight > .vue-treeselect__option-arrow-container,
.ec-tree-select .vue-treeselect__option:hover > .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__option--highlight > .vue-treeselect__checkbox-container {
  background: #ffffff !important;
}

.ec-tree-select .vue-treeselect__checkbox-container {
  width: 24px;
  min-width: 24px;
  position: relative;
  top: -3px;
}

.ec-tree-select .vue-treeselect__indent-level-1 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-2 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-3 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-4 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-5 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-6 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-7 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-8 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-9 .vue-treeselect__checkbox-container,
.ec-tree-select .vue-treeselect__indent-level-10 .vue-treeselect__checkbox-container {
  left: 13px;
}

.ec-tree-select .vue-treeselect__checkbox {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 1px solid #333;
  background: #fff;
}

.ec-tree-select .vue-treeselect__check-mark,
.ec-tree-select .vue-treeselect__minus-mark {
  left: 50%;
  top: 50%;
  margin-left: -4px;
  margin-top: -4px;
}

.ec-tree-select .vue-treeselect__label-container:hover .vue-treeselect__checkbox {
  box-shadow: 0 0 0 20px #d0d9f5;
  clip-path: circle(90%);
}

.ec-tree-select .vue-treeselect__checkbox--checked,
.ec-tree-select .vue-treeselect__label-container:hover .vue-treeselect__checkbox--checked,
.ec-tree-select .vue-treeselect__checkbox--indeterminate,
.ec-tree-select .vue-treeselect__label-container:hover .vue-treeselect__checkbox--indeterminate {
  background: #0e47cb;
  border-color: #0e47cb;
}

.ec-tree-select:deep(.vue-treeselect__multi-value) {
  max-width: 100%;
}

.ec-tree-select:not(.vue-treeselect--open):deep(.vue-treeselect__value-container) {
  max-height: 41px;
  overflow: hidden !important;
  white-space: nowrap;
  align-content: flex-start;
}

.ec-tree-select:not(.vue-treeselect--open):deep(.vue-treeselect__multi-value) {
  max-height: 41px;
  white-space: nowrap;
  overflow: hidden;
}

.ec-tree-select:deep(.vue-treeselect__multi-value-label) {
  display: inline-block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ec-tree-select .tree-option-row {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 0.5rem;
}

.ec-tree-select .tree-option-text {
  display: block;
  min-width: 0;
  flex: 1 1 auto;
  white-space: normal;
  overflow-wrap: anywhere;
  margin-left: 5px;
  margin-top: -8px;
}

.ec-tree-select .vue-treeselect__indent-level-1 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-2 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-3 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-4 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-5 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-6 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-7 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-8 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-9 .tree-option-text,
.ec-tree-select .vue-treeselect__indent-level-10 .tree-option-text {
  margin-left: 18px;
}

.ec-tree-select .tree-option-count {
  margin-left: auto;
  align-self: flex-start;
  margin-top: -8px;
  text-align: right;
  white-space: nowrap;
}

.ec-tree-select .tree-value-label {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.ec-tree-select .tree-select-badge {
  align-items: center;
  background-color: #0e47cb;
  border-radius: 0.75rem;
  color: #fff;
  display: inline-flex;
  justify-content: center;
  min-width: 35px;
  padding: 0.25rem;
  text-align: center;
  margin-right: 5px;
}

.ec-tree-select .tree-value-text {
  display: inline-block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tree-debug {
  margin-top: 0.75rem;
  padding: 0.5rem;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fafafa;
}

.tree-debug summary {
  cursor: pointer;
  font-size: 0.875rem;
}

.tree-debug pre {
  margin: 0.5rem 0 0;
  max-height: 260px;
  overflow: auto;
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
